import React, { useState, useEffect, useRef } from 'react';
import './styles.css';
import downArrow from '../../images/arrowDown.svg';
import worldIcon from '../../images/worldIcon.svg';
import aetnaLogoSimple from '../../images/aetnaLogoSimple.svg';
import closeIcon from '../../images/close.svg';
import mercyCareLogo from '../../images/MercyCareLogo.svg';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { useTranslation } from '../../contexts/TranslationContext';
import ohioRiseLogo from '../../images/OhioRiseLogo.svg';

const Header = () => {
    const { planFeatures, planCode, getFooterLinks, getAllowedLanguages } = usePlanFeatures();
    const { selectedLanguage, handleLanguageChange } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    // Separate open/expanded state (and ref, for outside-click detection) for the
    // "Languages" accordion inside the new full-width mobile nav panel — kept independent
    // from the desktop language dropdown's state/ref above since the two are rendered as
    // distinct DOM subtrees (only one of which is ever visible at a time, per viewport
    // width) and a single ref can only ever point at one of them at once.
    const [isMobileLanguageOpen, setIsMobileLanguageOpen] = useState(false);
    
    // Ref for language dropdown to detect outside clicks
    const languageDropdownRef = useRef(null);
    const mobileLanguageRef = useRef(null);
    
    // Get footer links from API
    const footerLinks = getFooterLinks();
    
    // Get allowed languages from plan features
    const allowedLanguages = getAllowedLanguages();

    // AZ is the only plan code mapped to the Mercy Care brand (mirrors
    // MERCYCARE_PLAN_CODES in App.jsx/server.js) — show the Mercy Care logo instead of
    // the standard Aetna Better Health logo for that plan.
    const isMercyCare = planCode === 'AZ';
    const isOhioRise = planCode === 'OH-RISE';

    // Use logoRedirectURL from API, fallback to Florida default
    const logoUrl = footerLinks?.logoRedirect || 'https://www.aetnabetterhealth.com/florida/index.html';
    // Was `|| 'https://www.aetnabetterhealth.com/florida/contact-us.html'` — a hardcoded,
    // state-specific fallback that silently sent every OTHER state's plan (PA, etc.) to
    // Florida's contact page whenever footerLinks.contactUs wasn't yet available,
    // instead of just failing safely. null here (see the "Contact Us" link render
    // below, which now conditionally omits the <a> entirely) means the link simply
    // isn't shown rather than ever misdirecting the user to a guessed, likely-wrong
    // state's page.
    const contactUsUrl = footerLinks?.contactUs || null;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
                setIsLanguageDropdownOpen(false);
            }
        };

        // Add event listener when dropdown is open
        if (isLanguageDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Cleanup event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLanguageDropdownOpen]);

    // Same outside-click-to-close behavior as the desktop dropdown above, but for the
    // mobile nav panel's own "Languages" accordion/ref — kept as a separate effect since
    // it has its own independent open state and DOM ref (see the comment on
    // isMobileLanguageOpen's declaration above for why these can't share one).
    useEffect(() => {
        const handleClickOutsideMobile = (event) => {
            if (mobileLanguageRef.current && !mobileLanguageRef.current.contains(event.target)) {
                setIsMobileLanguageOpen(false);
            }
        };

        if (isMobileLanguageOpen) {
            document.addEventListener('mousedown', handleClickOutsideMobile);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutsideMobile);
        };
    }, [isMobileLanguageOpen]);


    // Reset the mobile nav panel's own bits of state (language accordion collapsed) every
    // time the panel itself closes, so reopening it later doesn't briefly flash the
    // language list still expanded from a previous open/close cycle.
    useEffect(() => {
        if (!isMobileMenuOpen) {
            setIsMobileLanguageOpen(false);
        }
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleLanguageDropdown = () => {
        setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
    };

    const toggleMobileLanguageAccordion = () => {
        setIsMobileLanguageOpen(!isMobileLanguageOpen);
    };

    const handleLanguageSelect = (language) => {
        setIsLanguageDropdownOpen(false);
        setIsMobileLanguageOpen(false);
        handleLanguageChange(language); // Call TranslationContext handler
    };

    // Mobile-panel-specific version of handleLanguageSelect: also closes the entire
    // full-width mobile nav panel (not just the "Languages" accordion within it) once a
    // language is picked, matching the desktop dropdown's own auto-close-on-select
    // behavior instead of leaving the panel open over the (now-translated) page.
    const handleMobileLanguageSelect = (language) => {
        handleLanguageSelect(language);
        setIsMobileMenuOpen(false);
    };

    return (
        <header className="header container-fluid">
            <div className="row" id="headerTopRow">
                <div className="col" id="logoDiv">
                    {/* AC1 & AC2: Aetna logo displayed prominently, clickable to state-specific page */}
                    <div className="logo-container" translate="no">
                        <a 
                            href={logoUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label={isMercyCare ? "Mercy Care - Go to homepage" : isOhioRise ? "OhioRISE - Go to homepage" : "Aetna Better Health - Go to homepage"}
                        >
                            {isMercyCare ? (
                                <img
                                    src={mercyCareLogo}
                                    id="headerImage"
                                    width="260"
                                    height="80"
                                    alt="Mercy Care Logo"
                                />
                            ) : isOhioRise ? (
                                <img
                                    src={ohioRiseLogo}
                                    id="headerImage"
                                    width="203"
                                    height="44"
                                    alt="OhioRISE Logo"
                                />
                            ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" id="headerImage" width="160" height="32" viewBox="0 0 160 32" fill="none" role="img" aria-labelledby="logo-title">
                                <title id="logo-title">Aetna Better Health Logo</title>
                                <g clipPath="url(#clip0_481_333)">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M156.415 11.1442C154.678 11.1442 153.605 9.82943 153.605 8.22092C153.605 6.49776 154.835 5.29681 156.415 5.29681C157.975 5.29681 159.203 6.49776 159.203 8.22092C159.203 9.95162 157.975 11.1442 156.415 11.1442ZM156.415 4.68086C154.483 4.68086 152.826 6.09186 152.826 8.22092C152.826 10.2362 154.318 11.7593 156.415 11.7593C158.326 11.7593 159.984 10.35 159.984 8.22092C159.984 6.09186 158.326 4.68086 156.415 4.68086ZM155.693 8.02091V6.87687H156.492C156.892 6.87687 157.369 6.93378 157.369 7.40663C157.369 7.97488 156.931 8.02091 156.434 8.02091H155.693ZM158.034 7.45433C158.034 6.65928 157.536 6.32787 156.561 6.32787H155.03V10.2454H155.693V8.57075H156.317L157.361 10.2454H158.111L156.98 8.52304C157.584 8.45693 158.034 8.16318 158.034 7.45433ZM26.6075 5.04826C25.8502 4.31012 24.8601 3.94105 23.8699 3.94105C22.8754 3.94105 21.881 4.31263 21.1203 5.05244L16.6146 9.42521L12.1011 5.04658C11.3447 4.31012 10.3494 3.94021 9.35662 3.94021C8.36474 3.94021 7.372 4.30761 6.61645 5.04575L1.13268 10.365C-0.378421 11.8313 -0.378421 14.2164 1.13785 15.6852L16.6146 30.7024L32.0904 15.6852C33.6049 14.2173 33.6006 11.8338 32.0878 10.365L26.6075 5.04826ZM127.241 19.2847C127.241 17.3079 127.284 14.6784 127.284 14.6784C127.284 8.76156 124.608 6.10442 117.048 6.10442C112.805 6.10442 109.781 6.82666 105.042 6.82666C105.306 10.9802 105.317 16.1112 105.297 19.2847C105.318 22.5193 105.187 28.3064 105.042 30.1718H113.165C112.979 28.2997 112.816 22.916 112.838 19.2847C112.838 17.2184 112.866 14.7144 112.948 11.3568C113.755 11.0865 114.784 10.8864 115.899 10.8864C118.743 10.8864 119.659 12.1811 119.659 15.7387C119.659 15.7387 119.701 17.9691 119.701 19.2847C119.701 22.5193 119.592 28.3064 119.446 30.1718H127.572C127.383 28.2997 127.22 22.916 127.241 19.2847ZM141.565 26.1187C138.97 26.1187 137.871 25.2793 137.871 23.1771C137.871 21.1551 139.273 20.0027 142.81 20.0027C143.132 20.0027 143.53 19.991 143.814 20.0211C143.801 21.982 143.751 24.2684 143.871 25.8551C143.258 26.0141 142.348 26.1187 141.565 26.1187ZM151.582 15.009C151.582 8.45777 148.79 6.08684 141.344 6.08684C137.701 6.08684 134.492 6.61409 132.535 7.3857L132.825 12.6129C134.53 11.73 137.069 10.8848 139.494 10.8848C142.904 10.8848 143.933 12.2447 143.933 15.5237V16.0074C143.694 16.0015 143.1 15.9957 142.772 15.9957C134.278 15.9957 130.356 18.4888 130.356 23.1101C130.356 27.8712 132.8 30.6715 139.754 30.6715C143.423 30.6715 147.041 30.1693 151.702 30.1693C151.366 28.2637 151.354 25.5187 151.354 23.6307C151.354 21.5158 151.582 18.5139 151.582 15.009ZM99.5448 25.4668C97.0453 25.4668 96.3104 24.5069 96.3104 20.9802V11.8832H101.247V6.82666H96.3872C96.3898 5.03821 96.5312 2.3953 96.6917 0.574219H92.117C91.5089 4.9947 90.1962 7.2677 86.0346 7.93805C86.0173 9.12895 86.0829 10.7475 86.0346 11.8815H88.8075C88.7653 14.3922 88.6833 18.9759 88.6833 23.1553C88.6833 28.4888 91.5313 30.674 96.8805 30.674C99.4844 30.674 101.402 30.3099 102.035 30.1317L101.704 24.932C101.238 25.2342 100.32 25.4668 99.5448 25.4668ZM69.7851 16.2836V16.1672C69.7851 12.8883 70.8589 10.4805 73.9587 10.4805C75.7148 10.4805 76.8516 11.3743 76.8516 12.9988C76.8516 15.2843 75.2853 16.3447 72.0198 16.3447C71.2307 16.3447 70.3975 16.3321 69.7851 16.2836ZM47.4153 26.1187C44.8261 26.1187 43.7212 25.2793 43.7212 23.1771C43.7212 21.1551 45.1228 20.0027 48.659 20.0027C48.9876 20.0027 49.3852 19.991 49.6699 20.0211C49.6509 21.982 49.6069 24.2684 49.7208 25.8551C49.1075 26.0141 48.2045 26.1187 47.4153 26.1187ZM72.9298 20.3216C80.4068 20.3216 84.2466 18.0486 84.2466 13.1519C84.2466 8.92391 81.0821 6.11697 74.2063 6.11697C65.8754 6.11697 62.541 9.7265 61.7889 16.1848C60.5701 16.1112 59.2445 16.081 57.4315 16.0743C57.4315 15.7262 57.4375 15.3705 57.4375 15.009C57.4375 8.45777 54.6405 6.08684 47.1936 6.08684C43.5565 6.08684 40.3428 6.61409 38.3841 7.3857L38.6747 12.6129C40.3799 11.73 42.9251 10.8848 45.3444 10.8848C48.7539 10.8848 49.7898 12.2447 49.7898 15.5237L49.7837 16.0074C49.55 16.0015 48.9497 15.9957 48.6211 15.9957C40.1341 15.9957 36.2054 18.4888 36.2054 23.1101C36.2054 27.8712 38.6497 30.6715 45.6023 30.6715C49.2723 30.6715 52.8913 30.1693 57.5583 30.1693C57.2167 28.2637 57.2047 25.5187 57.2047 23.6307C57.2047 22.6381 57.2547 21.4489 57.3116 20.1006C59.3005 20.1199 60.772 20.1684 61.738 20.2111C62.453 27.8712 66.5447 30.7024 75.0584 30.7024C78.2031 30.7024 81.3857 30.1819 83.0529 29.4278L82.7623 24.2006C81.1071 25.1321 78.8034 25.8919 76.2771 25.8919C72.0897 25.8919 69.8282 23.7721 69.8282 20.4262V20.2663C70.5743 20.3032 72.0578 20.3216 72.9298 20.3216Z" fill="#7D3F98" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_481_333">
                                        <rect width="160" height="31.05" fill="white" transform="translate(0 0.574219)" />
                                    </clipPath>
                                </defs>
                            </svg>
                            )}
                        </a>
                        {!isMercyCare && !isOhioRise && (
                            <span className="logo-text">Aetna Better Health®</span>
                        )}
                    </div>
                </div>
                
                {/* Hamburger menu button for mobile */}
                <div className="col-auto d-md-none">
                    <button 
                        className="hamburger-menu" 
                        onClick={toggleMobileMenu}
                        aria-label="Toggle navigation menu"
                        aria-expanded={isMobileMenuOpen}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_874_4459)">
                                <path d="M22.75 17.75C23.4404 17.75 24 18.3096 24 19C24 19.6904 23.4404 20.25 22.75 20.25H1.25C0.559644 20.25 0 19.6904 0 19C0 18.3096 0.559644 17.75 1.25 17.75H22.75ZM22.75 10.75C23.4404 10.75 24 11.3096 24 12C24 12.6904 23.4404 13.25 22.75 13.25H1.25C0.559644 13.25 0 12.6904 0 12C0 11.3096 0.559644 10.75 1.25 10.75H22.75ZM22.75 3.75C23.4404 3.75 24 4.30964 24 5C24 5.69036 23.4404 6.25 22.75 6.25H1.25C0.559644 6.25 0 5.69036 0 5C0 4.30964 0.559644 3.75 1.25 3.75H22.75Z" fill="#1A1A19"/>
                            </g>
                            <defs>
                                <clipPath id="clip0_874_4459">
                                    <rect width="24" height="24" fill="white"/>
                                </clipPath>
                            </defs>
                        </svg>
                    </button>
                </div>

                <nav id="headerNav" className="col text-end d-none d-md-block">
                    {/* Language selector dropdown */}
                    <div className="language-dropdown-container" ref={languageDropdownRef} translate="no">
                        <button 
                            className="headerLink language-dropdown-toggle" 
                            onClick={toggleLanguageDropdown}
                            aria-label="Select language"
                            aria-expanded={isLanguageDropdownOpen}
                        >
                            <img src={worldIcon} className="worldIcon" alt="purple globe"/> 
                            {selectedLanguage}
                            <img src={downArrow} className="worldIcon" alt="purple arrow pointing down"/>
                        </button>
                        {isLanguageDropdownOpen && (
                            <div className="language-dropdown-menu">
                                {allowedLanguages.map((language) => (
                                    <button 
                                        key={language}
                                        className="language-dropdown-item"
                                        onClick={() => handleLanguageSelect(language)}
                                    >
                                        {language}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {/* AC4 & AC5: Contact Us link in top-right corner, opens in new tab.
                        Only rendered once we have a real, plan-specific URL — see
                        contactUsUrl's comment above for why this no longer falls back to
                        a hardcoded (and potentially wrong-state) default. */}
                    {contactUsUrl && (
                        <a 
                            href={contactUsUrl} 
                            className="headerLink contactUs" 
                            target="_blank" 
                            rel="noopener noreferrer"
                        >
                            Contact Us
                        </a>
                    )}
                </nav>
            </div>

            {/* Full-width mobile nav panel — replaces the old approach of toggling the
                desktop <nav> above visible via a CSS class override on mobile. This is a
                completely separate element, mutually exclusive with the desktop nav via the
                d-md-none/d-none d-md-block utility classes (same pattern already used for the
                hamburger button above), so there's no shared visibility state to coordinate
                between the two beyond `isMobileMenuOpen` itself. */}
            {isMobileMenuOpen && (
                <div
                    id="mobileNavPanel"
                    className="mobile-nav-panel d-md-none"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Navigation menu"
                >
                    <div className="mobile-nav-panel-header">
                        {isOhioRise ? (
                            <img src={ohioRiseLogo} alt="OhioRISE" className="mobile-nav-panel-logo mobile-nav-panel-logo-ohiorise" />
                        ) : (
                            <img src={aetnaLogoSimple} alt="Aetna" className="mobile-nav-panel-logo" />
                        )}
                        <button
                            type="button"
                            className="mobile-nav-panel-close"
                            onClick={toggleMobileMenu}
                            aria-label="Close navigation menu"
                        >
                            <img src={closeIcon} alt="" className="mobile-nav-panel-close-icon" />
                        </button>
                    </div>

                    <div className="mobile-nav-panel-content">
                        <div className="mobile-nav-panel-language" ref={mobileLanguageRef} translate="no">
                            <button
                                type="button"
                                className="mobile-nav-panel-language-toggle"
                                onClick={toggleMobileLanguageAccordion}
                                aria-expanded={isMobileLanguageOpen}
                            >
                                <span>Languages</span>
                                <img
                                    src={downArrow}
                                    alt=""
                                    className={`mobile-nav-panel-chevron${isMobileLanguageOpen ? ' mobile-nav-panel-chevron-open' : ''}`}
                                />
                            </button>
                            {isMobileLanguageOpen && (
                                <div className="mobile-nav-panel-language-list">
                                    {allowedLanguages.map((language) => (
                                        <button
                                            key={language}
                                            type="button"
                                            className="mobile-nav-panel-language-item"
                                            onClick={() => handleMobileLanguageSelect(language)}
                                        >
                                            {language}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="mobile-nav-panel-divider" role="separator" aria-hidden="true" />

                        {contactUsUrl && (
                            <a
                                href={contactUsUrl}
                                className="mobile-nav-panel-contact"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Contact Us
                            </a>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;